import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAMPAIGN_TYPES = [
  { value: "lancamento", label: "Lançamento / promoção" },
  { value: "evento", label: "Evento / ativação presencial" },
  { value: "pesquisa", label: "Pesquisa / feedback contínuo" },
  { value: "cliente_oculto", label: "Cliente oculto / auditoria" },
  { value: "outro", label: "Outro" },
];

const BUDGET_RANGES = [
  { value: "<30k", label: "Até R$ 30 mil" },
  { value: "30k-100k", label: "R$ 30 mil – R$ 100 mil" },
  { value: "100k-300k", label: "R$ 100 mil – R$ 300 mil" },
  { value: "300k+", label: "Acima de R$ 300 mil" },
  { value: "definir", label: "Ainda definindo" },
];

const INITIAL = {
  name: "",
  role: "",
  company: "",
  email: "",
  phone: "",
  campaign_type: "",
  budget_range: "",
  objectives: "",
  message: "",
};

export default function CampaignLeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.company || !form.campaign_type || !form.email) {
      toast.error("Preencha nome, empresa, tipo de campanha e e-mail.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/campaign-leads`, {
        ...form,
        role: form.role || null,
        phone: form.phone || null,
        budget_range: form.budget_range || null,
        objectives: form.objectives || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Recebemos seu briefing. Em até 5 dias úteis retornamos.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="piloto"
      data-testid="campaign-form-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute -top-32 -right-20 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #FA9F22, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -left-20 h-[400px] w-[400px] rounded-full blur-3xl opacity-20"
        style={{
          background: "radial-gradient(closest-side, #D94A0A, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#FA9F22]">
            Briefing, não checkout
          </span>
          <h2
            data-testid="campaign-form-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.96] text-[#F5FAFB]"
          >
            Quero desenhar uma campanha com a RSM.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F5FAFB]/70 max-w-md">
            Vamos entender seu desafio e montar um piloto sob medida. Retorno em
            até 5 dias úteis, sem compromisso.
          </p>

          <div className="mt-10 space-y-4 text-sm text-[#F5FAFB]/75 max-w-sm">
            {[
              "Co-criação de missões com o seu time.",
              "Painel de resultados em tempo real.",
              "Modelo piloto com tarifa especial de cases.",
            ].map((t, i) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#FA9F22] flex-shrink-0"
                />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="campaign-form-success"
              className="rounded-2xl border border-[#FA9F22]/30 bg-white/5 backdrop-blur p-10 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FA9F22]/20 text-[#FA9F22]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#F5FAFB]">
                Briefing recebido.
              </h3>
              <p className="mt-3 text-[#F5FAFB]/70 max-w-md mx-auto">
                Em até 5 dias úteis alguém da RSM Campanhas retorna com uma
                primeira proposta de piloto.
              </p>
              <button
                data-testid="campaign-form-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-[#F5FAFB]/85 hover:border-[#FA9F22] hover:text-[#FA9F22] transition-colors"
              >
                Enviar outro briefing
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="campaign-form"
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Seu nome *">
                  <input
                    data-testid="campaign-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nome completo"
                    className={inputCls}
                  />
                </Field>
                <Field label="Cargo / função">
                  <input
                    data-testid="campaign-input-role"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Ex.: Head de growth, gerente de marca"
                    className={inputCls}
                  />
                </Field>
                <Field label="Empresa *">
                  <input
                    data-testid="campaign-input-company"
                    required
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Nome da empresa"
                    className={inputCls}
                  />
                </Field>
                <Field label="Tipo de campanha *">
                  <select
                    data-testid="campaign-input-type"
                    required
                    value={form.campaign_type}
                    onChange={set("campaign_type")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {CAMPAIGN_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="campaign-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="voce@empresa.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefone / WhatsApp">
                  <input
                    data-testid="campaign-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(11) 90000-0000"
                    className={inputCls}
                  />
                </Field>
                <Field label="Faixa de investimento" className="sm:col-span-2">
                  <select
                    data-testid="campaign-input-budget"
                    value={form.budget_range}
                    onChange={set("budget_range")}
                    className={inputCls}
                  >
                    <option value="">Selecione (opcional)…</option>
                    {BUDGET_RANGES.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Principais objetivos da campanha" className="mt-4">
                <textarea
                  data-testid="campaign-input-objectives"
                  value={form.objectives}
                  onChange={set("objectives")}
                  rows={3}
                  placeholder="Ex.: aumentar awareness do lançamento X, ativar Black Friday, testar conceito de produto…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <Field label="Algo mais relevante?" className="mt-4">
                <textarea
                  data-testid="campaign-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Contexto, prazos, regiões prioritárias…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="campaign-form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#FA9F22] px-7 py-4 text-base font-semibold text-[#1B1D1D] shadow-sm transition-all duration-300 ease-out hover:bg-[#E68F1A] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#FA9F22] focus:ring-offset-2 focus:ring-offset-[#1B1D1D]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Quero desenhar uma campanha com a RSM
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#F5FAFB]/55">
                Vamos entender seu desafio e montar um piloto sob medida. Seus
                dados não são compartilhados.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#0e0f0f] px-4 py-3 text-sm text-[#F5FAFB] placeholder:text-[#F5FAFB]/30 focus:border-[#FA9F22] focus:outline-none focus:ring-2 focus:ring-[#FA9F22]/30 transition-all";
